/**
 * User POI Distance Service
 * 
 * Provides utilities for calculating User POI positions relative to routes
 */

import type { RouteData } from '@cya-routes/shared'
import type { UserPOI } from '../types/userPois'

export interface UserPOIDistanceResult {
  distance: number
  isOffRoute: boolean
  offRouteDistance: number
}

/**
 * Calculate User POI distance and position along a route
 * @param userPoi - User POI with coordinates
 * @param route - Route data with coordinates and elevation profile
 * @returns Object with distance along route, off-route status, and off-route distance
 */
export const calculateUserPOIDistance = (userPoi: UserPOI, route: RouteData): UserPOIDistanceResult => {
  if (!userPoi.coordinates || !route.coordinates || !route.elevation?.profile) {
    return { distance: 0, isOffRoute: true, offRouteDistance: 0 }
  }

  const routeCoordinates = route.coordinates
  const elevationData = route.elevation.profile
  
  let closestDistance = Infinity
  let closestRouteDistance = 0

  routeCoordinates.forEach((coord, index) => {
    const [routeLng, routeLat] = coord
    const distance = Math.sqrt(
      Math.pow(routeLat - userPoi.coordinates.lat, 2) + 
      Math.pow(routeLng - userPoi.coordinates.lng, 2)
    )

    if (distance < closestDistance) {
      closestDistance = distance
      // Find corresponding elevation data point
      const elevationPoint = elevationData[index]
      if (elevationPoint) {
        closestRouteDistance = elevationPoint.distance
      }
    }
  })

  // Convert degrees to approximate meters (rough approximation)
  const distanceInMeters = closestDistance * 111000
  const isOffRoute = distanceInMeters > 3000 // 3km threshold

  return {
    distance: closestRouteDistance,
    isOffRoute,
    offRouteDistance: distanceInMeters / 1000 // Convert to km
  }
}

/**
 * Calculate User POI distances for multiple User POIs along a route
 * @param userPois - Array of User POIs
 * @param route - Route data
 * @returns Array of User POI distance results
 */
export const calculateMultipleUserPOIDistances = (
  userPois: UserPOI[], 
  route: RouteData
): (UserPOIDistanceResult & { userPoi: UserPOI })[] => {
  return userPois.map(userPoi => ({
    userPoi,
    ...calculateUserPOIDistance(userPoi, route)
  }))
}

/**
 * Sort User POIs by their distance along the route
 * @param userPois - Array of User POIs with distance results
 * @returns Sorted array of User POIs
 */
export const sortUserPOIsByDistance = (
  userPoisWithDistance: (UserPOIDistanceResult & { userPoi: UserPOI })[]
): (UserPOIDistanceResult & { userPoi: UserPOI })[] => {
  return userPoisWithDistance.sort((a, b) => a.distance - b.distance)
}